/**
 * VqeStatementView — the Visual Query Editor's statement surface: statement
 * picker header, readiness banner, drill breadcrumbs and the statement's
 * cards (`VqeStatementBody`). Hosted by the Query Editor's Visual view
 * (`mode/QueryVisualModePanel`), which supplies the Monaco-backed `wiring`
 * and what "undo" and "edit as SQL" mean.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { Switch } from '../../../../components/ui/switch';
import { TooltipProvider } from '../../../../components/ui/tooltip';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { DatabaseEngineId } from '../../../../core/engines';
import { useConnectionsUIStore } from '../../../connections/store/uiStore';
import type { ParseOptions } from '../../../sql-grammar/model';
import type { VqeTabWiring } from '../edit/useVqeEditWiring';
import { buildDmlEstimate } from '../execute/buildDmlCountProbeSql';
import { useDmlRowEstimate } from '../execute/useDmlRowEstimate';
import { DmlBlastRadiusChip } from '../render/common/DmlBlastRadiusChip';
import { DrillBreadcrumbs } from '../render/select/DrillBreadcrumbs';
import { useVqeStore } from '../store/vqeStore';
import { useCommitFeedbackToasts } from '../ux/useCommitFeedbackToasts';
import { useEditAppliedStatus } from '../ux/useEditAppliedStatus';
import { useEditHighlight } from '../ux/useEditHighlight';
import { isTextEntryTarget } from '../ux/useVqeUndoShortcut';
import { useVqeReadiness } from '../ux/useVqeReadiness';
import { VqeEditStatus } from '../ux/VqeEditStatus';
import { VqeReadinessBanner, type VqeReadinessState } from '../ux/VqeReadinessBanner';
import { VqeShortcutsHelp } from '../ux/VqeShortcutsHelp';
import { VqeStatementPicker } from '../ux/VqeStatementPicker';
import { VqeTooltip } from '../render/common/VqeTooltip';
import { FALLBACK_REASONS, type ClassificationResult } from './classifyTabQuery';
import { useVqeTabDrill } from './useVqeTabDrill';
import { VqeStatementBody } from './VqeStatementBody';

// TODO(vqe-copy): consolidate into vqeCopy.ts along with the compact
// popover's "Run on change" copy (compact/sections/CompactVqeFooter.tsx) once
// the sole vqeCopy.ts writer for that batch has landed — see
// task-prim6a-degradedetail-brief.md.
const RUN_ON_CHANGE_LABEL = 'Run on change';
const RUN_ON_CHANGE_ON_TIP = 'Re-runs this SELECT after every edit here. Also under Settings → UI → Visual editor.';
const RUN_ON_CHANGE_OFF_TIP = 'Turn on to re-run this SELECT after every edit here. Also under Settings → UI → Visual editor.';

export type VqeStatementViewProps = {
  readonly tabId: string;
  /** The buffer the view renders. */
  readonly query: string;
  readonly classification: ClassificationResult | null;
  readonly options: ParseOptions;
  readonly engineId: DatabaseEngineId;
  readonly connectionId?: string;
  readonly database?: string;
  readonly wiring: VqeTabWiring;
  readonly isActivePane: boolean;
  /** A run is in flight: the DML estimate probe waits, and the "Edit applied" status clears. */
  readonly isExecuting: boolean;
  /** SR-Edit document id this surface's commits carry (drives its "Edit applied" status). */
  readonly documentId: string;
  /** "Run on change" — the same setting the compact popover's footer switch reads/writes (SELECT only). */
  readonly runOnChange: { readonly enabled: boolean; readonly onToggle: () => void };
  /** The live buffer right now. */
  readonly readSource: () => string;
  /** Undo the last edit ("Edit applied · Undo"); false when nothing was undone. */
  readonly undoEdit: () => boolean;
  readonly onSelectStatement: (startOffset: number) => void;
  /** Leave the cards for the SQL text, at `errorOffset` for a parse error. */
  readonly onEditAsSql: (errorOffset: number | null) => void;
  readonly onChooseDatabase?: () => void;
  readonly onSeedQuery: (sql: string) => void;
  readonly rootRef?: React.RefObject<HTMLDivElement>;
  readonly className?: string;
};

export function VqeStatementView(props: VqeStatementViewProps) {
  const {
    tabId,
    query,
    classification,
    options,
    engineId,
    connectionId,
    database,
    wiring,
    isActivePane,
    isExecuting,
    documentId,
    runOnChange,
    onEditAsSql,
    onChooseDatabase,
  } = props;
  const { track } = useAnalytics();
  // Commit-feedback toasts (every failure). The hook is multi-mount-safe.
  useCommitFeedbackToasts();
  const { editContext, service } = wiring;
  const drill = useVqeTabDrill(tabId, classification, query, options, editContext);
  const statement = classification?.statement ?? null;
  const mode = classification?.renderMode;
  const source = classification?.buffer.parsedDocument.source ?? query;

  // "Edit applied · Undo" for this surface's own edits, in its header.
  const status = useEditAppliedStatus({ documentId, query, readSource: props.readSource });
  const ownRootRef = useRef<HTMLDivElement>(null);
  const rootRef = props.rootRef ?? ownRootRef;
  useEditHighlight({ getRoot: () => rootRef.current, active: status.visible, editToken: status.editToken });
  const { dismiss: dismissStatus } = status;
  // A run clears the status: the results bar confirms the run, and the status
  // would otherwise sit next to it describing the edit.
  useEffect(() => {
    if (isExecuting) dismissStatus();
  }, [isExecuting, dismissStatus]);
  const handleStatusUndo = () => {
    if (props.undoEdit()) track('feature:vqe_undo', { direction: 'undo', via: 'status', surface: 'mode' });
    dismissStatus();
  };

  const isReadOnlyMode = mode != null && (mode === 'parse-error' || FALLBACK_REASONS.has(mode));
  const readiness = useVqeReadiness({ connectionId, database, isReadOnlyMode, service });
  const readinessState = readiness.state;
  const bannerDismissed = useVqeStore((s) => s.dismissedBanners.has(tabId));
  // § 1.5 — a fallback panel (read-only / parse-error) owns both the message
  // and the primary CTA, so the read-only banner is not shown on top of it.
  const showsFallbackPanel = classification != null && isReadOnlyMode && (mode === 'parse-error' || statement != null);
  const bannerState: VqeReadinessState =
    readinessState === 'read-only' && showsFallbackPanel ? 'ready' : readinessState;
  const handleBannerPrimaryAction = () => {
    if (readinessState === 'no-connection') useConnectionsUIStore.getState().setShowConnectionsDialog(true);
    else if (readinessState === 'snapshot-failed' || readinessState === 'columns-failed') readiness.reloadSchema();
    else if (readinessState === 'no-database') onChooseDatabase?.();
    else if (readinessState === 'read-only') onEditAsSql(null);
  };
  const handleDismissBanner = () => {
    useVqeStore.getState().dismissBanner(tabId);
    // Low-cardinality only — never the tabId itself.
    track('feature:vqe_readiness_banner_dismiss', { key: 'tab' });
  };

  const handleSelectStatement = (id: string) => {
    const statements = classification?.buffer.documentStructure.statements ?? [];
    const target = statements.find((s) => s.id === id);
    if (!target) return;
    // Drill offsets reference the prior statement's body — clear them.
    drill.clear();
    props.onSelectStatement(target.range.startOffset);
    track('feature:vqe_statement_picker_change', { count: statements.length });
  };

  // §A4 blast-radius estimate: the COUNT(*) probe for the active UPDATE/DELETE,
  // built from the SAME buffer + offsets the renderer uses.
  const dmlEstimate = useMemo(
    () => (statement ? buildDmlEstimate(mode, source, statement.range, options) : null),
    [statement, mode, source, options],
  );
  // Click-to-arm; suppressed while a real run is in flight (never interleave a
  // probe with Execute on the same transaction session).
  const dmlRowEstimate = useDmlRowEstimate({
    tabId,
    connectionId,
    database,
    engineId,
    verb: dmlEstimate?.verb ?? 'update',
    probeSql: dmlEstimate?.probe?.sql ?? null,
    isSuppressed: isExecuting,
  });

  // Keyboard-shortcut help, opened by the "?" button or by pressing "?" while
  // this pane is active (never while typing into an input / Monaco). A window
  // listener rather than onKeyDown on the root: the root is not focusable, so
  // "?" would be missed while focus rests on document.body.
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const openShortcutsHelp = useCallback(() => {
    setShortcutsOpen(true);
    track('feature:vqe_shortcuts_help_open');
  }, [track]);
  useEffect(() => {
    if (!isActivePane) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '?' || event.defaultPrevented || isTextEntryTarget(event.target)) return;
      event.preventDefault();
      openShortcutsHelp();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isActivePane, openShortcutsHelp]);

  // The picker's active id must be the TRUE classified statement.
  const statements = classification?.buffer.documentStructure.statements ?? [];
  const activeStatementId = statement?.id ?? statements[0]?.id ?? '';

  return (
    <TooltipProvider>
      <div
        ref={rootRef}
        tabIndex={-1}
        className={`flex h-full w-full flex-col bg-background outline-none ${props.className ?? ''}`}
        data-testid="visual-query-workspace"
        data-render-mode={classification?.renderMode ?? 'empty'}
        data-drill-depth={drill.stack.length}
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          <VqeStatementPicker
            statements={statements}
            activeStatementId={activeStatementId}
            onSelect={handleSelectStatement}
            bufferText={classification?.buffer.parsedDocument.source}
          />
          <div className="flex-1" />
          <VqeEditStatus visible={status.visible} onUndo={handleStatusUndo} />
          {dmlEstimate ? (
            <DmlBlastRadiusChip
              intent={dmlEstimate.verb}
              state={dmlRowEstimate.state}
              approximate={dmlEstimate.probe?.approximate ?? false}
              hasWhere={dmlEstimate.hasWhere}
              onArm={dmlRowEstimate.arm}
            />
          ) : null}
          {mode === 'select' ? (
            <VqeTooltip content={runOnChange.enabled ? RUN_ON_CHANGE_ON_TIP : RUN_ON_CHANGE_OFF_TIP}>
              <label className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
                <Switch
                  checked={runOnChange.enabled}
                  onCheckedChange={() => runOnChange.onToggle()}
                  aria-label="Run the query after every visual edit"
                  data-testid="vqe-visual-run-on-change"
                />
                {RUN_ON_CHANGE_LABEL}
              </label>
            </VqeTooltip>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground"
            onClick={openShortcutsHelp}
            aria-label="Keyboard shortcuts"
            data-testid="visual-query-shortcuts-button"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
        </div>

        <VqeShortcutsHelp open={shortcutsOpen} onOpenChange={setShortcutsOpen} />

        <VqeReadinessBanner
          variant="full"
          state={bannerState}
          detail={readiness.failureMessage}
          dismissed={bannerDismissed}
          onDismiss={handleDismissBanner}
          onPrimaryAction={handleBannerPrimaryAction}
        />

        <DrillBreadcrumbs stack={drill.stack} onJumpTo={drill.jumpTo} />

        <VqeStatementBody
          tabId={tabId}
          classification={classification}
          options={options}
          editContext={editContext}
          service={service}
          drill={drill}
          connectionId={connectionId}
          database={database}
          readinessState={readinessState}
          onEditAsSql={onEditAsSql}
          onReloadSchema={readiness.reloadSchema}
          onChooseDatabase={onChooseDatabase}
          onSeedQuery={props.onSeedQuery}
        />
      </div>
    </TooltipProvider>
  );
}
